import { Button, Modal } from '@orobix/orobix-fe-components'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { handleApiErrors } from '../../../utils/apiErrors'
import { slugify } from '../../../utils/charsHelper'
import { getEnvs } from '../../../utils/env'
import CompatibilityCheckSelect from '../CompatibilityCheckSelect/CompatibilityCheckSelect'

export interface UploadAnnotationClassMappingModalProps {
  onCloseRequest: () => void
  onConfirmRequest: () => void
  setUploading: (uploading: boolean) => void
  productId: number
  sourceProductClasses?: string[]
  targetProductClasses?: string[]
  annotationFileToUpload?: File
}

export const UploadAnnotationClassMappingModal = ({
  onCloseRequest,
  onConfirmRequest,
  setUploading,
  productId,
  sourceProductClasses,
  targetProductClasses,
  annotationFileToUpload,
}: UploadAnnotationClassMappingModalProps) => {
  const { t } = useTranslation()
  const { apiUrl } = getEnvs()

  const handleSubmitClassMapping = async (payload: string, file?: File) => {
    if (file === undefined) {
      toast.error(t('Error during annotations import'))
      setUploading(false)
      return
    }
    toast.success(t('Import started'))
    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('product_id', `${productId}`)
    formData.append('product_class_map', `${payload}`)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await axios({
      method: 'post',
      url: `${apiUrl}/aigo/datatransfer/import/`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(() => {
        toast.success(t('File successfully imported'))
      })
      .catch(handleApiErrors)
      .finally(() => {
        setUploading(false)
      })
  }

  const getPayloadForSubmit = () => {
    let payload = ''
    let first = true
    sourceProductClasses?.forEach((s) => {
      const id = `comp-chk-id-${slugify(s)}`
      const elem = document.querySelectorAll(
        `[id^=${id}]`
      )[0] as HTMLSelectElement
      let value = elem.innerText
      const valueToClear = t('No Label')
      if (value === valueToClear) {
        value = ''
      }
      if (first) {
        payload += `"${s}":"${value}"`
      } else {
        payload += `,"${s}":"${value}"`
      }
      first = false
    })
    payload = `{${payload}}`
    return payload
  }

  return (
    <Modal
      size="md"
      onCloseRequest={(event) => {
        event.stopPropagation()
        onCloseRequest()
      }}
      title={t('Warning: Manual Action Required')}
      footer={
        <>
          <Button
            onClick={(event) => {
              event.stopPropagation()
              onCloseRequest()
            }}
            label={t('Abort')}
            type="default"
            buttonType="button"
          />
          <Button
            data-testid="confirmUploadAnnotationButton"
            onClick={(event) => {
              event.stopPropagation()
              handleSubmitClassMapping(
                getPayloadForSubmit(),
                annotationFileToUpload
              )
              onConfirmRequest()
            }}
            label={t('Confirm')}
            type="action"
            buttonType="button"
          />
        </>
      }
    >
      {t(
        "The annotations you are trying to import require your attention. Please provide the correspondence between the original and destination labels and then press “Confirm”. If you do not want to assign any label, select 'No Label' (This will import the images associated with that label without any annotations)."
      )}
      <div>
        {sourceProductClasses &&
          targetProductClasses &&
          sourceProductClasses.map((s) => (
            <CompatibilityCheckSelect
              key={slugify(s)}
              fieldName={s}
              options={targetProductClasses}
              id={`comp-chk-id-${slugify(s)}`}
            />
          ))}
      </div>
    </Modal>
  )
}

export default UploadAnnotationClassMappingModal
